import React, { useState } from 'react';
import apiService, { FeedbackFamily, FeedbackResponse, FeedbackVerdict } from '../services/api';
import { CloseIcon } from './icons';
import './FeedbackModal.css';

export interface FlaggedResult {
  family: FeedbackFamily;
  modelVersion: string;
  cargo: string;
  // DGML's answer for this cargo, forwarded unaltered
  response?: unknown;
  // What the card showed, so the reporter can see what they are disagreeing with
  said: string;
}

interface FeedbackModalProps {
  flagged: FlaggedResult;
  onClose: () => void;
  onSubmitted: (response: FeedbackResponse) => void;
}

const DG_TITLE: Record<FeedbackFamily, string> = {
  dg: 'Correct the dangerous goods verdict',
  un: 'Correct the UN numbers',
  hs: 'Correct the HS code'
};

const MODEL_LABEL: Record<FeedbackFamily, string> = {
  dg: 'Dangerous goods',
  un: 'UN prediction',
  hs: 'HS prediction'
};

type DgChoice = 'dangerous' | 'not-dangerous' | 'undeterminable';

function parseUnNumbers(entered: string): string[] {
  return entered.split(/[,\s]+/).filter((part) => part !== '');
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ flagged, onClose, onSubmitted }) => {
  const [dgChoice, setDgChoice] = useState<DgChoice | ''>('');
  const [unEntered, setUnEntered] = useState('');
  const [noUnNumber, setNoUnNumber] = useState(false);
  const [hsCode, setHsCode] = useState('');
  const [note, setNote] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hsDigits = hsCode.replace(/[\s.]/g, '');
  const hsValid = /^\d{2}(\d{2}(\d{2})?)?$/.test(hsDigits);
  const unNumbers = parseUnNumbers(unEntered);

  const verdict: FeedbackVerdict | null =
    flagged.family === 'dg'
      ? (dgChoice === '' ? null : { isDg: dgChoice === 'undeterminable' ? null : dgChoice === 'dangerous' })
      : flagged.family === 'un'
        ? (noUnNumber ? { unNumbers: [] } : unNumbers.length > 0 ? { unNumbers } : null)
        : (hsValid ? { hsCode: hsDigits } : null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!verdict) {
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      const response = await apiService.submitFeedback({
        model: flagged.family,
        modelVersion: flagged.modelVersion,
        cargo: flagged.cargo,
        response: flagged.response,
        verdict,
        note: note.trim() || undefined
      });

      onSubmitted(response);
    } catch (err: any) {
      setError(err.response?.data?.error || 'The report was not saved. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="card modal-content feedback-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedbackTitle"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="feedbackTitle" className="h4">{DG_TITLE[flagged.family]}</h3>
          <button
            type="button"
            className="btn btn-flat modal-close"
            onClick={onClose}
            disabled={isSending}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="field">
              <span className="field-label">Cargo</span>
              <div className="flagged-cargo">{flagged.cargo}</div>
            </div>

            <div className="field">
              <span className="field-label">{MODEL_LABEL[flagged.family]} {flagged.modelVersion} said</span>
              <div className="flagged-said">{flagged.said}</div>
            </div>

            {flagged.family === 'dg' && (
              <fieldset className="field verdict-choices">
                <legend className="field-label">What is the right answer? *</legend>
                <label>
                  <input
                    type="radio"
                    name="dgVerdict"
                    value="dangerous"
                    checked={dgChoice === 'dangerous'}
                    onChange={() => setDgChoice('dangerous')}
                    disabled={isSending}
                  />
                  Dangerous goods
                </label>
                <label>
                  <input
                    type="radio"
                    name="dgVerdict"
                    value="not-dangerous"
                    checked={dgChoice === 'not-dangerous'}
                    onChange={() => setDgChoice('not-dangerous')}
                    disabled={isSending}
                  />
                  Not dangerous goods
                </label>
                <label>
                  <input
                    type="radio"
                    name="dgVerdict"
                    value="undeterminable"
                    checked={dgChoice === 'undeterminable'}
                    onChange={() => setDgChoice('undeterminable')}
                    disabled={isSending}
                  />
                  The text does not say
                </label>
                <p className="field-hint">
                  Pick the last one rather than guessing. It is recorded as a verdict of its own.
                </p>
              </fieldset>
            )}

            {flagged.family === 'un' && (
              <div className="field">
                <label htmlFor="unNumbers" className="field-label">Correct UN number(s) *</label>
                <input
                  id="unNumbers"
                  type="text"
                  className="input"
                  value={unEntered}
                  onChange={(event) => setUnEntered(event.target.value)}
                  placeholder="UN1263, UN1866"
                  disabled={isSending || noUnNumber}
                />
                <label className="inline-check">
                  <input
                    type="checkbox"
                    checked={noUnNumber}
                    onChange={(event) => setNoUnNumber(event.target.checked)}
                    disabled={isSending}
                  />
                  No UN number applies to this cargo
                </label>
              </div>
            )}

            {flagged.family === 'hs' && (
              <div className="field">
                <label htmlFor="hsCode" className="field-label">Correct HS code *</label>
                <input
                  id="hsCode"
                  type="text"
                  className="input"
                  value={hsCode}
                  onChange={(event) => setHsCode(event.target.value)}
                  placeholder="6110 or 611030"
                  disabled={isSending}
                />
                <p className="field-hint">
                  Give as many digits as you are sure of: 2 for the chapter, 4 for the heading,
                  6 for the subheading.
                </p>
                {hsCode !== '' && !hsValid && (
                  <p className="field-error">An HS code is 2, 4 or 6 digits.</p>
                )}
              </div>
            )}

            <div className="field">
              <label htmlFor="feedbackNote" className="field-label">Note</label>
              <textarea
                id="feedbackNote"
                className="input textarea"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Anything that explains the case, for whoever reviews it"
                rows={2}
                disabled={isSending}
              />
            </div>

            {error && <p className="field-error">{error}</p>}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-outline btn-md" disabled={isSending}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-md" disabled={isSending || !verdict}>
              {isSending ? 'Sending…' : 'Send report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
